/* One floating tray. Forms owns signup; the product controller owns configuration. */
(() => {
  if (customElements.get('kindra-welcome-offer')) return;
  customElements.define('kindra-welcome-offer', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.popup = window.KindraWelcomePopup ? new window.KindraWelcomePopup(this) : null;
      this.editorPreview = this.dataset.designMode === 'true';
      this.delayComplete = this.editorPreview || this.dataset.pageType === 'product';
      if (!this.delayComplete) this.revealTimer = setTimeout(() => {
        this.delayComplete = true;
        this.schedule();
      }, 8000);
      try { this.minimized = (sessionStorage.getItem('kindra-welcome-minimized') ?? sessionStorage.getItem('kindra-welcome-dismissed')) === '1'; } catch (_) {}
      if (this.editorPreview) this.minimized = false;
      // Start mobile non-product pages expanded, even after minimising on another page.
      if (this.dataset.pageType !== 'product' && window.matchMedia('(max-width: 999px)').matches) this.minimized = false;
      this.querySelector('[data-welcome-dismiss]').addEventListener('click', () => {
        this.minimized = true;
        try { sessionStorage.setItem('kindra-welcome-minimized', '1'); } catch (_) {}
        this.sync();
        if (!this.hidden) this.querySelector('[data-welcome-expand]').focus({ preventScroll: true });
      }, { signal: this.abort.signal });
      this.querySelector('[data-welcome-title]')?.addEventListener('click', event => this.openOffer(event.currentTarget), { signal: this.abort.signal });
      this.querySelector('[data-welcome-chevron]').addEventListener('click', event => this.openOffer(event.currentTarget), { signal: this.abort.signal });
      this.querySelector('[data-welcome-expand]').addEventListener('click', event => this.openOffer(event.currentTarget), { signal: this.abort.signal });
      this.querySelector('[data-welcome-open]').addEventListener('click', event => this.openOffer(event.currentTarget), { signal: this.abort.signal });
      this.querySelector('[data-welcome-review]').addEventListener('click', event => {
        this.product?.requestOrder?.(event.currentTarget);
      }, { signal: this.abort.signal });
      this.querySelector('[data-welcome-orb]').addEventListener('click', () => {
        if (this.footerCompact) {
          this.footerExpanded = true;
          this.sync();
          this.querySelector('[data-welcome-review]:not([disabled]), [data-welcome-expand]')?.focus({ preventScroll: true });
        } else this.openOffer();
      }, { signal: this.abort.signal });
      if (this.dataset.pageType === 'index') {
        this.scrollDesktop = window.matchMedia('(min-width: 1000px)');
        this.scrollAnchor = window.scrollY;
        window.addEventListener('scroll', () => this.handleHomeScroll(), { passive: true, signal: this.abort.signal });
        const expand = () => {
          if (this.footerCompact) return;
          clearTimeout(this.scrollIdleTimer);
          this.scrollCollapsed = false;
          const focusOrb = document.activeElement === this.querySelector('[data-welcome-orb]');
          this.sync();
          if (focusOrb) this.querySelector('[data-welcome-expand]').focus({ preventScroll: true });
        };
        this.addEventListener('pointerenter', expand, { signal: this.abort.signal });
        this.addEventListener('focusin', expand, { signal: this.abort.signal });
      }
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'hidden', 'aria-hidden', 'disabled'] });
      window.addEventListener('scroll', () => this.schedule(), { passive: true, signal: this.abort.signal });
      window.addEventListener('resize', () => this.schedule(), { passive: true, signal: this.abort.signal });
      document.addEventListener('shopify:section:load', () => this.schedule(), { signal: this.abort.signal });
      let attempts = 0;
      this.discovery = setInterval(() => { this.schedule(); if (++attempts >= 30 || this.host) clearInterval(this.discovery); }, 1000);
      this.schedule();
    }
    disconnectedCallback() {
      this.popup?.destroy();
      clearTimeout(this.scrollIdleTimer);
      this.trayTransition?.cancel();
      this.abort?.abort(); this.abort = null;
      this.observer?.disconnect(); this.shadowObserver?.disconnect();
      clearTimeout(this.revealTimer); clearTimeout(this.offerOpenTimer); this.openingOffer = false;
      clearInterval(this.discovery); cancelAnimationFrame(this.frame); this.frame = null;
      this.host?.removeAttribute('data-ke-managed-teaser');
    }
    handleHomeScroll() {
      const y = window.scrollY;
      if (!this.scrollDesktop?.matches || this.hidden || this.openingOffer ||
          this.matches(':hover') || this.querySelector(':focus-visible')) {
        this.scrollAnchor = y;
        return;
      }
      if (!this.scrollCollapsed && Math.abs(y - this.scrollAnchor) < 12) return;
      this.scrollAnchor = y;
      this.scrollCollapsed = true;
      this.homeScrollUsed = true;
      clearTimeout(this.scrollIdleTimer);
      this.scrollIdleTimer = setTimeout(() => {
        this.scrollCollapsed = false;
        this.schedule();
      }, 1000);
      this.schedule();
    }
    openOffer(trigger) {
      if (this.editorPreview && this.popup) {
        this.offerTrigger = trigger || this.querySelector('[data-welcome-title]');
        this.popup.openEditor(this.offerTrigger);
        return;
      }
      // Forms can replace its shadow content after closing or editor refreshes.
      this.sync();
      const teaser = this.host?.shadowRoot?.querySelector('[data-testid="form-teaser"]');
      if (!teaser || this.openingOffer) return;
      this.offerTrigger = trigger || this.querySelector((this.minimized || this.homeScrollUsed) ? '[data-welcome-expand]' : '[data-welcome-chevron]');
      this.popup?.prepare(this.offerTrigger);
      this.openingOffer = true;
      clearTimeout(this.offerOpenTimer);
      this.host.removeAttribute('data-ke-managed-teaser');
      // Keep the app visible while its asynchronous teaser-to-dialog transition runs.
      this.offerOpenTimer = setTimeout(() => {
        this.openingOffer = false;
        this.schedule();
      }, 2000);
      // Let the original outside click finish before opening the app dialog.
      requestAnimationFrame(() => { if (this.openingOffer && teaser.isConnected !== false) teaser.click(); });
      this.schedule();
    }
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.sync(); });
    }
    setHidden(element, value) { if (element.hidden !== !!value) element.hidden = !!value; }
    sync() {
      // Measure the current tray before the purchase row changes its layout.
      const previousBox = !this.hidden && this.getBoundingClientRect?.();
      const previousPurchase = this.purchaseWasVisible;
      const previousCollapsed = this.homeWasCollapsed;
      this.product = document.querySelector('kindra-elata-product');
      const inline = this.product?.querySelector('[data-ke-offer]');
      const formId = this.product?.dataset.keFormsId || inline?.dataset.keFormsId || this.dataset.formsId;
      if (!this.host?.isConnected || this.host.id !== `app-embed-container-${formId}`) {
        this.shadowObserver?.disconnect();
        this.host = document.getElementById(`app-embed-container-${formId}`);
        if (!this.host?.shadowRoot) this.host = null;
        else {
          const formStyle = document.getElementById('kindra-welcome-form-style');
          if (['619074', '1204553', '1207424', '1207428', '1207429', '1207504'].includes(formId) && formStyle && !this.host.shadowRoot.querySelector('[data-kindra-form-style]')) {
            const style = formStyle.content.firstElementChild.cloneNode(true);
            style.setAttribute('data-kindra-form-style', '');
            this.host.shadowRoot.appendChild(style);
          }
          this.shadowObserver = new MutationObserver(() => this.schedule());
          this.shadowObserver.observe(this.host.shadowRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-current-step', 'aria-label'] });
        }
      }
      if (this.host && !this.popup?.editorOpen) this.popup?.attach(this.host);
      const root = this.host?.shadowRoot?.querySelector('[data-layout][data-current-step]');
      if (!this.popup?.editorOpen) this.popup?.sync(root);
      const teaser = this.host?.shadowRoot?.querySelector('[data-testid="form-teaser"]');
      const ready = root?.dataset.currentStep === 'tease' && !!teaser;
      const canOpen = ready || this.editorPreview;
      if (ready && window.location.hash === '#kindra-welcome-offer' && !this.previewOfferRequested) {
        this.previewOfferRequested = true;
        requestAnimationFrame(() => this.openOffer());
      }
      if (this.dataset.offerEnabled === 'true' || inline) this.host?.toggleAttribute('data-ke-managed-teaser', ready && !this.openingOffer);
      const offerButton = this.querySelector('[data-welcome-open]');
      this.setHidden(offerButton, false);
      offerButton.disabled = !canOpen;
      this.querySelector('[data-welcome-chevron]').disabled = !canOpen;
      const titleButton = this.querySelector('[data-welcome-title]');
      if (titleButton) titleButton.disabled = !canOpen;
      const offerHint = '';
      if (offerButton.title !== offerHint) offerButton.title = offerHint;
      const offerLabel = 'Redeem';
      if (offerButton.textContent !== offerLabel) offerButton.textContent = offerLabel;
      const inlineBox = inline?.getBoundingClientRect();
      const inlineVisible = !!inlineBox && inlineBox.bottom > 0 && inlineBox.top < innerHeight && !inline.hidden;
      const formsOpen = this.popup?.editorOpen || (!!root && root.dataset.currentStep !== 'tease' && !!this.host.shadowRoot.querySelector('[role="dialog"]'));
      if (formsOpen && this.openingOffer) {
        this.openingOffer = false;
        clearTimeout(this.offerOpenTimer);
      }
      const modalOpen = [...document.querySelectorAll('dialog[open], .drawer[open], [role="dialog"][aria-modal="true"]:not([hidden]):not([aria-hidden="true"])')].some(element => element.hasAttribute('open') || (getComputedStyle(element).visibility !== 'hidden' && getComputedStyle(element).display !== 'none'));
      const order = this.product?.querySelector('[data-ke-order]');
      const purchaseVisible = this.product?.data?.purchaseVisible !== false && this.product?.data?.stickyEnabled !== false && !!order && order.getBoundingClientRect().bottom < 0 && !!this.product?.variant;
      // The editor uses a local preview; customer signup remains owned by Forms.
      const offerVisible = this.dataset.offerEnabled === 'true' && !inlineVisible && (ready || this.editorPreview) && this.delayComplete;
      const homeDesktop = this.dataset.pageType === 'index' && !!this.scrollDesktop?.matches;
      const footer = document.querySelector('.shopify-section--footer');
      const footerVisible = !!footer && footer.getBoundingClientRect().top < innerHeight;
      if (!footerVisible) this.footerExpanded = false;
      this.footerCompact = footerVisible && !this.footerExpanded;
      const collapsed = (this.footerCompact || (homeDesktop && !!this.scrollCollapsed)) && !formsOpen && !modalOpen && !this.openingOffer;
      this.toggleAttribute('data-footer-compact', this.footerCompact);
      this.querySelector('[data-welcome-purchase]').inert = collapsed;
      const minimized = !!this.minimized || (homeDesktop && !!this.homeScrollUsed);
      this.toggleAttribute('data-scroll-collapsed', collapsed);
      this.toggleAttribute('data-minimized', minimized);
      this.setHidden(this.querySelector('[data-welcome-expand]'), !minimized);
      const orb = this.querySelector('[data-welcome-orb]');
      if (orb) {
        this.setHidden(orb, !collapsed);
        orb.disabled = this.footerCompact ? false : !canOpen;
        orb.setAttribute('aria-label', this.footerCompact ? 'Expand quick access' : 'Redeem welcome offer');
        orb.querySelector('span').textContent = this.footerCompact && purchaseVisible ? 'View' : 'Offer';
      }
      const offer = this.querySelector('[data-welcome-offer]');
      offer.inert = collapsed;
      this.toggleAttribute('data-product', !!this.product);
      this.toggleAttribute('data-purchase-visible', purchaseVisible);
      this.toggleAttribute('data-offer-visible', offerVisible);
      this.setHidden(this.querySelector('[data-welcome-offer]'), !offerVisible);
      this.setHidden(this.querySelector('[data-welcome-purchase]'), !purchaseVisible);
      const price = this.product?.querySelector('[data-ke-sticky-price]')?.textContent || '';
      const priceTarget = this.querySelector('[data-welcome-price]');
      if (priceTarget.textContent !== price) {
        if (this.product?.crossfadePrice) this.product.crossfadePrice(priceTarget, price);
        else priceTarget.textContent = price;
      }
      const review = this.querySelector('[data-welcome-review]');
      const label = this.product?.data?.labels?.purchase || 'Place 50% Deposit';
      if (review.textContent !== label) review.textContent = label;
      const disabled = !this.product?.variant || !order || !!order.disabled;
      if (review.disabled !== disabled) review.disabled = disabled;
      const hide = (!this.delayComplete && !this.product) || formsOpen || modalOpen || (!purchaseVisible && !offerVisible);
      this.setHidden(this, hide);
      if (!hide && !this.hasEntered) { this.setAttribute('data-entered', ''); this.hasEntered = true; }
      if (this.wasFormsOpen && !formsOpen && this.offerTrigger && !hide) {
        this.offerTrigger.focus({ preventScroll: true }); this.offerTrigger = null;
      }
      this.wasFormsOpen = formsOpen;
      this.purchaseWasVisible = purchaseVisible;
      this.homeWasCollapsed = collapsed;
      if (previousBox && !hide && previousCollapsed !== undefined && previousCollapsed !== collapsed &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches && this.animate) {
        this.trayTransition?.cancel();
        const nextBox = this.getBoundingClientRect();
        this.trayTransition = this.animate([
          { width: previousBox.width + 'px', height: previousBox.height + 'px', overflow: 'clip' },
          { width: nextBox.width + 'px', height: nextBox.height + 'px', overflow: 'clip' }
        ], { duration: 240, easing: 'cubic-bezier(.22, 1, .36, 1)' });
      }
      if (previousBox && !hide && previousPurchase !== undefined && previousPurchase !== purchaseVisible &&
          !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches && this.animate) {
        this.trayTransition?.cancel();
        const nextBox = this.getBoundingClientRect();
        this.trayTransition = this.animate([
          { width: previousBox.width + 'px', height: previousBox.height + 'px', overflow: 'clip' },
          { width: nextBox.width + 'px', height: nextBox.height + 'px', overflow: 'clip' }
        ], { duration: 320, easing: 'cubic-bezier(.22, 1, .36, 1)' });
        if (purchaseVisible) {
          this.querySelector('[data-welcome-purchase]').animate?.([
            { opacity: 0, transform: 'translateY(6px)' },
            { opacity: 1, transform: 'translateY(0)' }
          ], { duration: 280, delay: 40, easing: 'ease-out', fill: 'backwards' });
        }
      }
    }
  });
})();


